import React, { useState } from 'react';
import { 
  FiFileText, 
  FiTrendingUp,
  FiUsers, 
  FiBell, 
  FiMenu,
  FiX,
  FiPlay,
  FiBarChart,
  FiLogOut
} from 'react-icons/fi';

interface AdminLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onPageChange: (page: string) => void;
  onLogout?: () => void;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, currentPage, onPageChange, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Get admin email from localStorage
  const adminEmail = localStorage.getItem('admin_email') || 'admin@mandilive.in';
  
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('admin_authenticated');
      localStorage.removeItem('admin_email');
      localStorage.removeItem('admin_login_time');
      if (onLogout) {
        onLogout();
      }
    }
  };

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: FiMenu },
    { id: 'crops', label: 'Crops', icon: FiFileText },
    { id: 'crop-price-table', label: 'Crop Price Table', icon: FiFileText },
    { id: 'volumes', label: 'Volumes', icon: FiFileText },
    { id: 'trending-news', label: 'Trending News', icon: FiTrendingUp },
    { id: 'news-section', label: 'News Section', icon: FiFileText },
    { id: 'mandi-bhav-videos', label: 'Mandi Bhav Videos', icon: FiPlay },
    { id: 'bubble-videos', label: 'Bubble Videos', icon: FiPlay },
    { id: 'government-schemes', label: 'Government Schemes', icon: FiFileText },
    { id: 'market-conditions', label: 'Market Conditions', icon: FiBarChart },
    { id: 'users', label: 'Users', icon: FiUsers },
    { id: 'notifications', label: 'Notifications', icon: FiBell },
    { id: 'settings', label: 'Settings', icon: FiUsers },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-green-600 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col`}>
        <div className="flex items-center justify-between h-16 px-6 bg-green-700 flex-shrink-0">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center mr-3">
              <span className="text-green-600 font-bold text-lg">🌾</span>
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">Mandi Live</h1>
              <p className="text-green-200 text-xs">Admin Panel</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white hover:text-green-200"
          >
            <FiX size={24} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 py-6">
          <div className="mb-4">
            <p className="text-green-200 text-xs uppercase tracking-wider font-semibold px-3">
              NAVIGATION
            </p>
          </div>
          <ul className="space-y-2 pb-6">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => {
                      onPageChange(item.id);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === item.id
                        ? 'bg-green-700 text-white'
                        : 'text-green-100 hover:bg-green-700 hover:text-white'
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-600 hover:text-gray-900 mr-4"
              >
                <FiMenu size={24} />
              </button>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Admin Dashboard</h2>
                <p className="text-sm text-gray-600">Manage your mandi and news data</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">A</span>
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-gray-800">Admin User</p>
                  <p className="text-xs text-gray-600">{adminEmail}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Logout"
                >
                  <FiLogOut className="h-4 w-4" />
                  <span className="ml-1 hidden sm:inline">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
